import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  PenTool,
  PenLine,
  Calendar,
  Type,
  CheckSquare,
  GripVertical,
  Loader2,
  AlertTriangle,
} from "lucide-react";

// Set the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FieldDef {
  documentIndex: number;
  signerIndex: number;
  type: "signature" | "initials" | "date_signed" | "text" | "checkbox";
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label: string;
}

export interface SignerInfo {
  name: string;
  color: string;
}

export interface FieldPlacementEditorProps {
  fields: FieldDef[];
  onFieldsChange: (fields: FieldDef[]) => void;
  signers: SignerInfo[];
  /** Color for sender fields (signerIndex = -1) */
  senderColor?: string;
  currentDocumentIndex: number;
  currentPage: number;
  activeSigner: number;
  scale?: number;
  /** Presigned URL to the PDF document to render as background */
  documentUrl?: string;
  /** Called when the PDF loads and reports its total page count */
  onPageCountChange?: (count: number) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_WIDTH = 612; // US Letter in points
const PAGE_HEIGHT = 792;

export const FIELD_TYPES = [
  { type: "signature" as const, label: "Signature", icon: PenTool, defaultWidth: 20, defaultHeight: 5 },
  { type: "initials" as const, label: "Initials", icon: PenLine, defaultWidth: 10, defaultHeight: 5 },
  { type: "date_signed" as const, label: "Date Signed", icon: Calendar, defaultWidth: 15, defaultHeight: 4 },
  { type: "text" as const, label: "Text Field", icon: Type, defaultWidth: 20, defaultHeight: 4 },
  { type: "checkbox" as const, label: "Checkbox", icon: CheckSquare, defaultWidth: 3, defaultHeight: 3 },
];

const FIELD_ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  signature: PenTool,
  initials: PenLine,
  date_signed: Calendar,
  text: Type,
  checkbox: CheckSquare,
};

// ---------------------------------------------------------------------------
// Grid Background Component
// ---------------------------------------------------------------------------

const GridBackground: React.FC<{ width: number; height: number }> = ({ width, height }) => {
  const gridSize = 20;
  const cols = Math.ceil(width / gridSize);
  const rows = Math.ceil(height / gridSize);

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      style={{ opacity: 0.3 }}
    >
      {Array.from({ length: cols + 1 }, (_, i) => (
        <line
          key={`v-${i}`}
          x1={i * gridSize}
          y1={0}
          x2={i * gridSize}
          y2={height}
          stroke="#cbd5e1"
          strokeWidth={i % 5 === 0 ? 0.5 : 0.25}
        />
      ))}
      {Array.from({ length: rows + 1 }, (_, i) => (
        <line
          key={`h-${i}`}
          x1={0}
          y1={i * gridSize}
          x2={width}
          y2={i * gridSize}
          stroke="#cbd5e1"
          strokeWidth={i % 5 === 0 ? 0.5 : 0.25}
        />
      ))}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// FieldPlacementEditor
// ---------------------------------------------------------------------------

const FieldPlacementEditor: React.FC<FieldPlacementEditorProps> = ({
  fields,
  onFieldsChange,
  signers,
  senderColor = "#374151",
  currentDocumentIndex,
  currentPage,
  activeSigner,
  scale = 0.75,
  documentUrl,
  onPageCountChange,
}) => {
  const [selectedField, setSelectedField] = useState<number | null>(null);
  const [draggingField, setDraggingField] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState<{
    fieldIndex: number;
    handle: "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";
    startMouseX: number;
    startMouseY: number;
    startWidth: number;
    startHeight: number;
    startFieldX: number;
    startFieldY: number;
  } | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);

  // Memoize the file prop so react-pdf doesn't restart the worker on every render
  const pdfFile = useMemo(() => (documentUrl ? { url: documentUrl } : null), [documentUrl]);

  const displayWidth = PAGE_WIDTH * scale;
  const displayHeight = PAGE_HEIGHT * scale;

  // Reset PDF states when document URL changes
  useEffect(() => {
    if (documentUrl) {
      setPdfLoading(true);
      setPdfError(false);
    } else {
      setPdfLoading(false);
      setPdfError(false);
    }
  }, [documentUrl]);

  // PDF load handlers
  const handlePdfLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setPdfLoading(false);
      setPdfError(false);
      onPageCountChange?.(numPages);
    },
    [onPageCountChange]
  );

  const handlePdfLoadError = useCallback(() => {
    setPdfLoading(false);
    setPdfError(true);
  }, []);

  // Fields visible on the current document + page
  const currentPageFields = fields.filter(
    (f) => f.documentIndex === currentDocumentIndex && f.page === currentPage
  );

  // Map from current-page index back to the global fields array index
  const globalIndexForLocal = useCallback(
    (localIdx: number): number => {
      let count = -1;
      for (let i = 0; i < fields.length; i++) {
        if (
          fields[i].documentIndex === currentDocumentIndex &&
          fields[i].page === currentPage
        ) {
          count++;
          if (count === localIdx) return i;
        }
      }
      return -1;
    },
    [fields, currentDocumentIndex, currentPage]
  );

  // -----------------------------------------------------------------------
  // Drop handler — new field from palette
  // -----------------------------------------------------------------------

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const fieldType = e.dataTransfer.getData("fieldType");
      if (!fieldType) return;

      const fieldConfig = FIELD_TYPES.find((f) => f.type === fieldType);
      if (!fieldConfig || !pageRef.current) return;

      const rect = pageRef.current.getBoundingClientRect();
      const dropX = ((e.clientX - rect.left) / rect.width) * 100;
      const dropY = ((e.clientY - rect.top) / rect.height) * 100;

      // Clamp to page bounds
      const clampedX = Math.max(0, Math.min(dropX, 100 - fieldConfig.defaultWidth));
      const clampedY = Math.max(0, Math.min(dropY, 100 - fieldConfig.defaultHeight));

      const newField: FieldDef = {
        documentIndex: currentDocumentIndex,
        signerIndex: activeSigner,
        type: fieldConfig.type,
        page: currentPage,
        x: clampedX,
        y: clampedY,
        width: fieldConfig.defaultWidth,
        height: fieldConfig.defaultHeight,
        required: true,
        label: fieldConfig.label,
      };

      onFieldsChange([...fields, newField]);
      setSelectedField(fields.length); // Select the newly added field
    },
    [fields, onFieldsChange, currentDocumentIndex, currentPage, activeSigner]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  // -----------------------------------------------------------------------
  // Mouse-based move for existing fields
  // -----------------------------------------------------------------------

  const startFieldDrag = useCallback(
    (e: React.MouseEvent, localIdx: number) => {
      e.stopPropagation();
      e.preventDefault();

      const globalIdx = globalIndexForLocal(localIdx);
      if (globalIdx === -1 || !pageRef.current) return;

      setSelectedField(globalIdx);
      setDraggingField(globalIdx);

      const rect = pageRef.current.getBoundingClientRect();
      const field = fields[globalIdx];
      const fieldPxX = (field.x / 100) * rect.width;
      const fieldPxY = (field.y / 100) * rect.height;
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      setDragOffset({
        x: mouseX - fieldPxX,
        y: mouseY - fieldPxY,
      });
    },
    [fields, globalIndexForLocal]
  );

  useEffect(() => {
    if (draggingField === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const field = fields[draggingField];

      const newX = ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100;
      const newY = ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100;

      const clampedX = Math.max(0, Math.min(newX, 100 - field.width));
      const clampedY = Math.max(0, Math.min(newY, 100 - field.height));

      const updated = [...fields];
      updated[draggingField] = { ...updated[draggingField], x: clampedX, y: clampedY };
      onFieldsChange(updated);
    };

    const handleMouseUp = () => {
      setDraggingField(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingField, dragOffset, fields, onFieldsChange]);

  // -----------------------------------------------------------------------
  // Mouse-based resize for existing fields
  // -----------------------------------------------------------------------

  const MIN_WIDTH_PCT = 3;
  const MIN_HEIGHT_PCT = 2;

  const startFieldResize = useCallback(
    (e: React.MouseEvent, globalIdx: number, handle: "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w") => {
      e.stopPropagation();
      e.preventDefault();

      const field = fields[globalIdx];
      if (!field) return;

      setResizing({
        fieldIndex: globalIdx,
        handle,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startWidth: field.width,
        startHeight: field.height,
        startFieldX: field.x,
        startFieldY: field.y,
      });
    },
    [fields]
  );

  useEffect(() => {
    if (resizing === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();

      const { fieldIndex, handle, startMouseX, startMouseY, startWidth, startHeight, startFieldX, startFieldY } = resizing;

      // Delta in percentage of page dimensions
      const deltaXPct = ((e.clientX - startMouseX) / rect.width) * 100;
      const deltaYPct = ((e.clientY - startMouseY) / rect.height) * 100;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startFieldX;
      let newY = startFieldY;

      // Determine which axes are affected and in which direction
      const resizesRight = handle === "e" || handle === "se" || handle === "ne";
      const resizesLeft = handle === "w" || handle === "sw" || handle === "nw";
      const resizesBottom = handle === "s" || handle === "se" || handle === "sw";
      const resizesTop = handle === "n" || handle === "ne" || handle === "nw";

      if (resizesRight) {
        newWidth = startWidth + deltaXPct;
      }
      if (resizesLeft) {
        newWidth = startWidth - deltaXPct;
        newX = startFieldX + deltaXPct;
      }
      if (resizesBottom) {
        newHeight = startHeight + deltaYPct;
      }
      if (resizesTop) {
        newHeight = startHeight - deltaYPct;
        newY = startFieldY + deltaYPct;
      }

      // Enforce minimum sizes
      if (newWidth < MIN_WIDTH_PCT) {
        if (resizesLeft) {
          newX = startFieldX + startWidth - MIN_WIDTH_PCT;
        }
        newWidth = MIN_WIDTH_PCT;
      }
      if (newHeight < MIN_HEIGHT_PCT) {
        if (resizesTop) {
          newY = startFieldY + startHeight - MIN_HEIGHT_PCT;
        }
        newHeight = MIN_HEIGHT_PCT;
      }

      // Clamp to page bounds
      if (newX < 0) {
        newWidth = newWidth + newX; // shrink width by the overshoot
        newX = 0;
      }
      if (newY < 0) {
        newHeight = newHeight + newY;
        newY = 0;
      }
      if (newX + newWidth > 100) {
        newWidth = 100 - newX;
      }
      if (newY + newHeight > 100) {
        newHeight = 100 - newY;
      }

      // Final min-size guard after clamping
      newWidth = Math.max(newWidth, MIN_WIDTH_PCT);
      newHeight = Math.max(newHeight, MIN_HEIGHT_PCT);

      const updated = [...fields];
      updated[fieldIndex] = {
        ...updated[fieldIndex],
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      };
      onFieldsChange(updated);
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing, fields, onFieldsChange]);

  // -----------------------------------------------------------------------
  // Select / deselect
  // -----------------------------------------------------------------------

  const handlePageClick = useCallback(() => {
    setSelectedField(null);
  }, []);

  const handleFieldClick = useCallback(
    (e: React.MouseEvent, localIdx: number) => {
      e.stopPropagation();
      const globalIdx = globalIndexForLocal(localIdx);
      setSelectedField(globalIdx);
    },
    [globalIndexForLocal]
  );

  // -----------------------------------------------------------------------
  // Remove field
  // -----------------------------------------------------------------------

  const removeField = useCallback(
    (globalIdx: number) => {
      const updated = fields.filter((_, i) => i !== globalIdx);
      onFieldsChange(updated);
      setSelectedField(null);
    },
    [fields, onFieldsChange]
  );

  // -----------------------------------------------------------------------
  // Reassign signer for a field
  // -----------------------------------------------------------------------

  const reassignSigner = useCallback(
    (globalIdx: number, newSignerIdx: number) => {
      const updated = [...fields];
      updated[globalIdx] = { ...updated[globalIdx], signerIndex: newSignerIdx };
      onFieldsChange(updated);
    },
    [fields, onFieldsChange]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="flex flex-col items-center">
      {/* Page area */}
      <div
        ref={pageRef}
        className="relative bg-white border border-slate-300 shadow-lg select-none"
        style={{
          width: displayWidth,
          height: displayHeight,
          cursor: resizing !== null ? "grabbing" : draggingField !== null ? "grabbing" : "default",
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handlePageClick}
      >
        {/* PDF background — shown when a document URL is provided and loaded */}
        {documentUrl && !pdfError ? (
          <>
            <div className="absolute inset-0 pointer-events-none">
              <Document
                file={pdfFile}
                onLoadSuccess={handlePdfLoadSuccess}
                onLoadError={handlePdfLoadError}
                loading={null}
              >
                <Page
                  pageNumber={currentPage}
                  width={displayWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </div>

            {/* Loading spinner overlay while PDF is loading */}
            {pdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 pointer-events-none z-[5]">
                <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-2" />
                <span className="text-sm text-slate-500">Loading document...</span>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Grid background — fallback when no PDF URL or PDF failed to load */}
            <GridBackground width={displayWidth} height={displayHeight} />

            {/* Page watermark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-slate-200 text-6xl font-bold select-none" style={{ opacity: 0.25 }}>
                Page {currentPage}
              </span>
            </div>

            {/* Page margin guides */}
            <div
              className="absolute pointer-events-none border border-dashed border-slate-200"
              style={{
                top: "5%",
                left: "5%",
                right: "5%",
                bottom: "5%",
              }}
            />

            {/* PDF error message */}
            {pdfError && documentUrl && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-[5]">
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 pointer-events-auto">
                  <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <span className="text-xs text-amber-700">
                    Could not load PDF preview. You can still place fields using the grid.
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Field overlays */}
        {currentPageFields.map((field, localIdx) => {
          const globalIdx = globalIndexForLocal(localIdx);
          const isSelected = selectedField === globalIdx;
          const isSenderField = field.signerIndex === -1;
          const fieldColor = isSenderField
            ? senderColor
            : signers[field.signerIndex]?.color || "#6B7280";
          const FieldIcon = FIELD_ICON_MAP[field.type] || Type;
          const signerName = isSenderField
            ? "Me (Sender)"
            : signers[field.signerIndex]?.name || "Unknown";

          return (
            <div
              key={`field-${globalIdx}`}
              className={`absolute rounded-lg flex items-center justify-center text-xs font-medium select-none ${
                isSelected ? "shadow-lg" : "hover:shadow-md"
              }`}
              style={{
                left: `${field.x}%`,
                top: `${field.y}%`,
                width: `${field.width}%`,
                height: `${field.height}%`,
                border: isSelected ? `2.5px solid ${fieldColor}` : `2px solid ${fieldColor}`,
                backgroundColor: `${fieldColor}${isSelected ? "28" : "18"}`,
                color: fieldColor,
                cursor: draggingField === globalIdx ? "grabbing" : "grab",
                zIndex: isSelected ? 20 : 10,
              }}
              onMouseDown={(e) => startFieldDrag(e, localIdx)}
              onClick={(e) => handleFieldClick(e, localIdx)}
            >
              <FieldIcon className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{field.label || field.type}</span>

              {/* Signer badge */}
              {isSelected && (
                <div
                  className="absolute -bottom-6 left-0 whitespace-nowrap text-[10px] font-semibold px-1.5 py-0.5 rounded shadow-sm"
                  style={{
                    backgroundColor: fieldColor,
                    color: "#fff",
                    zIndex: 30,
                  }}
                >
                  {signerName}
                </div>
              )}

              {/* Drag handle indicator */}
              <GripVertical
                className="absolute top-0.5 left-0.5 h-3 w-3 opacity-40"
                style={{ color: fieldColor }}
              />

              {/* Resize handles — only visible when field is selected */}
              {isSelected && (
                <>
                  {/* Corner handles */}
                  {(["nw", "ne", "sw", "se"] as const).map((handle) => (
                    <div
                      key={handle}
                      className="absolute"
                      style={{
                        width: 8,
                        height: 8,
                        backgroundColor: "#fff",
                        border: `2px solid ${fieldColor}`,
                        borderRadius: 1,
                        zIndex: 31,
                        cursor: `${handle}-resize`,
                        ...(handle.includes("n") ? { top: -4 } : { bottom: -4 }),
                        ...(handle.includes("w") ? { left: -4 } : { right: -4 }),
                      }}
                      onMouseDown={(e) => startFieldResize(e, globalIdx, handle)}
                    />
                  ))}
                  {/* Edge handles */}
                  {(["n", "s", "e", "w"] as const).map((handle) => {
                    const isHorizontal = handle === "n" || handle === "s";
                    return (
                      <div
                        key={handle}
                        className="absolute"
                        style={{
                          zIndex: 31,
                          backgroundColor: "#fff",
                          border: `2px solid ${fieldColor}`,
                          borderRadius: 1,
                          cursor: isHorizontal ? "ns-resize" : "ew-resize",
                          ...(isHorizontal
                            ? {
                                width: 10,
                                height: 6,
                                left: "50%",
                                transform: "translateX(-50%)",
                                ...(handle === "n" ? { top: -3 } : { bottom: -3 }),
                              }
                            : {
                                width: 6,
                                height: 10,
                                top: "50%",
                                transform: "translateY(-50%)",
                                ...(handle === "w" ? { left: -3 } : { right: -3 }),
                              }),
                        }}
                        onMouseDown={(e) => startFieldResize(e, globalIdx, handle)}
                      />
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
        {/* Floating selected field detail panel */}
        {selectedField !== null && selectedField < fields.length && (() => {
          const sf = fields[selectedField];
          const fieldCenterX = sf.x + sf.width / 2;
          const fieldTop = sf.y;
          const fieldBottom = sf.y + sf.height;
          const showBelow = fieldTop < 40;
          const popupWidthPct = 70;
          let popupLeft = fieldCenterX - popupWidthPct / 2;
          if (popupLeft < 1) popupLeft = 1;
          if (popupLeft + popupWidthPct > 99) popupLeft = 99 - popupWidthPct;

          const fieldTypeLabels: Record<string, string> = {
            signature: "Signature",
            initials: "Initials",
            date_signed: "Date Signed",
            text: "Text Field",
            checkbox: "Checkbox",
          };

          const fieldTypeIcons: Record<string, React.FC<any>> = {
            signature: PenTool,
            initials: PenLine,
            date_signed: Calendar,
            text: Type,
            checkbox: CheckSquare,
          };

          const FieldIcon = fieldTypeIcons[sf.type] || Type;

          return (
            <div
              className="absolute bg-white border border-slate-200 rounded-2xl shadow-2xl"
              style={{
                left: `${popupLeft}%`,
                width: `${popupWidthPct}%`,
                ...(showBelow
                  ? { top: `${fieldBottom + 2}%` }
                  : { bottom: `${100 - fieldTop + 2}%` }),
                zIndex: 40,
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <FieldIcon className="h-3.5 w-3.5 text-slate-600" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-700">
                      {fieldTypeLabels[sf.type] || sf.type}
                    </span>
                    {sf.label && sf.type !== "text" && (
                      <span className="text-xs text-slate-400 ml-1.5">— {sf.label}</span>
                    )}
                  </div>
                </div>
                <button
                  className="text-xs text-red-400 hover:text-red-600 font-medium px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                  onClick={() => removeField(selectedField)}
                >
                  Remove
                </button>
              </div>

              {/* Body */}
              <div className="p-4 space-y-4">
                {/* Label / Rename */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">
                    Field Label
                  </label>
                  <input
                    type="text"
                    value={sf.label || ""}
                    onChange={(e) => {
                      const updated = [...fields];
                      updated[selectedField] = { ...updated[selectedField], label: e.target.value };
                      onFieldsChange(updated);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    placeholder={
                      sf.type === "text" ? "E.g., Full Legal Name, Address, Phone Number..."
                      : sf.type === "signature" ? "E.g., Client Signature, Witness Signature..."
                      : sf.type === "initials" ? "E.g., Client Initials..."
                      : sf.type === "date_signed" ? "E.g., Date Signed, Execution Date..."
                      : "Enter a label..."
                    }
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    {sf.type === "text"
                      ? "This label tells the signer what to enter in this field"
                      : "Optional label shown to the signer for this field"}
                  </p>
                </div>

                {/* Assign to — 2-column grid */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-2">
                    Assign to
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Sender option */}
                    <button
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all border ${
                        sf.signerIndex === -1
                          ? "ring-2 ring-offset-1 shadow-sm border-transparent"
                          : "border-slate-150 opacity-60 hover:opacity-100 hover:border-slate-300"
                      }`}
                      style={{
                        backgroundColor: sf.signerIndex === -1 ? `${senderColor}12` : undefined,
                        color: sf.signerIndex === -1 ? senderColor : undefined,
                        "--tw-ring-color": sf.signerIndex === -1 ? senderColor : undefined,
                      } as React.CSSProperties}
                      onClick={() => reassignSigner(selectedField, -1)}
                    >
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0 border-2"
                        style={{ borderColor: senderColor, backgroundColor: sf.signerIndex === -1 ? senderColor : "transparent" }}
                      />
                      Me (Sender)
                    </button>
                    {signers.map((signer, idx) => (
                      <button
                        key={idx}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all border ${
                          sf.signerIndex === idx
                            ? "ring-2 ring-offset-1 shadow-sm border-transparent"
                            : "border-slate-150 opacity-60 hover:opacity-100 hover:border-slate-300"
                        }`}
                        style={{
                          backgroundColor: sf.signerIndex === idx ? `${signer.color}12` : undefined,
                          color: sf.signerIndex === idx ? signer.color : undefined,
                          "--tw-ring-color": sf.signerIndex === idx ? signer.color : undefined,
                        } as React.CSSProperties}
                        onClick={() => reassignSigner(selectedField, idx)}
                      >
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0 border-2"
                          style={{ borderColor: signer.color, backgroundColor: sf.signerIndex === idx ? signer.color : "transparent" }}
                        />
                        {signer.name || `Signer ${idx + 1}`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Required toggle */}
                <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                  <span className="text-xs font-medium text-slate-500">Required field</span>
                  <button
                    onClick={() => {
                      const updated = [...fields];
                      updated[selectedField] = { ...updated[selectedField], required: !updated[selectedField].required };
                      onFieldsChange(updated);
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
                      sf.required ? "bg-emerald-500" : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        sf.required ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default FieldPlacementEditor;
